// =============================================
// LISTA DE GASTOS - MEJORADA
// Con exportación, ordenamiento y paginación
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TableSortLabel,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  GetApp as ExportIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import expenseService from '../../services/expenseService';
import api from '../../services/api';
import ExpenseForm from './ExpenseForm';
import ImageViewer from '../Common/ImageViewer';
import CategoryModal from './CategoryModal';

const ExpenseList = () => {
  // EXPENSES: Estados
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [searchYearsBack, setSearchYearsBack] = useState(3);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  // FILTROS: Estados
  const [filters, setFilters] = useState({
    category_id: '',
    year: new Date().getFullYear(),
    month: '',
  });

  // ORDENAMIENTO: Estados
  const [orderBy, setOrderBy] = useState('expense_date');
  const [order, setOrder] = useState('desc');

  // PAGINACIÓN: Estados
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // EXPENSES: Cargar datos al montar
  useEffect(() => {
    loadSystemParameters();
    loadExpenses();
    loadCategories();
  }, []);


  // EXPENSES: Cargar parámetros del sistema
  const loadSystemParameters = async () => {
    try {
      const response = await api.get('/system-parameters');
      const params = response.data.parameters || [];
      const searchYearsParam = params.find(p => p.param_key === 'search_years_back');

      if (searchYearsParam) {
        setSearchYearsBack(parseInt(searchYearsParam.param_value) || 3);
      }
    } catch (err) {
      console.error('Error al cargar parámetros del sistema:', err);
      // Mantener el valor por defecto de 3
    }
  };
  // FILTROS Y ORDENAMIENTO: Aplicar cuando cambian
  useEffect(() => {
    applyFiltersAndSort();
  }, [filters, expenses, orderBy, order]);


  // EXPENSES: Cargar gastos
  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await expenseService.getAll();
      setExpenses(response.expenses || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar gastos');
    } finally {
      setLoading(false);
    }
  };

  // CATEGORIES: Cargar categorías
  const loadCategories = async () => {
    try {
      const response = await expenseService.getCategories();
      setCategories(response.categories || []);
    } catch (err) {
      console.error('Error al cargar categorías:', err);
    }
  };

  // FILTROS: Aplicar filtros y ordenamiento
  const applyFiltersAndSort = () => {
    let filtered = [...expenses];

    // Filtrar por categoría
    if (filters.category_id) {
      filtered = filtered.filter(e => e.category_id === parseInt(filters.category_id));
    }

    // Filtrar por año
    if (filters.year) {
      filtered = filtered.filter(e => {
        const expenseYear = new Date(e.expense_date).getFullYear();
        return expenseYear === parseInt(filters.year);
      });
    }

    // Filtrar por mes
    if (filters.month) {
      filtered = filtered.filter(e => {
        const expenseMonth = new Date(e.expense_date).getMonth() + 1;
        return expenseMonth === parseInt(filters.month);
      });
    }

    // Ordenar
    filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Para fechas
      if (orderBy === 'expense_date' || orderBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Para números
      if (orderBy === 'amount') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      // Para strings
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredExpenses(filtered);
  };

  // FILTROS: Cambiar filtro
  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value
    });
    setPage(0); // Reset página al filtrar
  };

  // FILTROS: Limpiar filtros
  const handleClearFilters = () => {
    setFilters({
      category_id: '',
      year: new Date().getFullYear(),
      month: '',
    });
  };

  // ORDENAMIENTO: Cambiar ordenamiento
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // PAGINACIÓN: Cambiar página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // PAGINACIÓN: Cambiar filas por página
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // EXPORT: Exportar a Excel
  const handleExportToExcel = async () => {
    if (!filteredExpenses || filteredExpenses.length === 0) {
      setSnackbar({ open: true, message: 'No hay datos para exportar', severity: 'warning' });
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Gastos');

      // Columnas
      worksheet.columns = [
        { header: 'ID', key: 'id', width: 8 },
        { header: 'Categoría', key: 'category', width: 25 },
        { header: 'Descripción', key: 'description', width: 45 },
        { header: 'Monto', key: 'amount', width: 15, style: { numFmt: '"B/."#,##0.00' } },
        { header: 'Fecha', key: 'date', width: 15 },
        { header: 'Notas', key: 'notes', width: 35 },
        { header: 'Registrado por', key: 'created_by', width: 25 },
        { header: 'Fecha de Registro', key: 'created_at', width: 20 }
      ];

      // Estilo Encabezado
      const headerRow = worksheet.getRow(1);
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF004D4D' }
        };
        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Datos
      filteredExpenses.forEach((expense, index) => {
        const row = worksheet.addRow({
          id: expense.id,
          category: expense.category_name,
          description: expense.description,
          amount: parseFloat(expense.amount),
          date: formatDate(expense.expense_date),
          notes: expense.notes || '',
          created_by: expense.created_by_name,
          created_at: formatDate(expense.created_at)
        });

        // Estilos de fila (incluyendo celdas vacías para bordes)
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'middle' };

          if (index % 2 === 0) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF9F9F9' }
            };
          }
        });
      });

      // Agregar fila de totales
      const totalAmount = filteredExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
      const totalRow = worksheet.addRow({
        description: 'TOTAL GASTOS',
        amount: totalAmount
      });

      totalRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE0F2F1' } // Verde muy claro
        };
        cell.border = {
          top: { style: 'medium' },
          left: { style: 'thin' },
          bottom: { style: 'medium' },
          right: { style: 'thin' }
        };
      });

      // Filtros y Paneles
      worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];
      worksheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: 8 } };

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `gastos_${new Date().toISOString().split('T')[0]}.xlsx`);

      setSnackbar({
        open: true,
        message: `Exportados ${filteredExpenses.length} gastos a Excel`,
        severity: 'success',
      });

    } catch (err) {
      console.error('Error al exportar Excel:', err);
      setSnackbar({ open: true, message: 'Error al generar Excel', severity: 'error' });
    }
  };

  // EXPENSES: Abrir formulario para crear
  const handleCreate = () => {
    setSelectedExpense(null);
    setOpenForm(true);
  };

  // EXPENSES: Abrir formulario para editar
  const handleEdit = (expense) => {
    setSelectedExpense(expense);
    setOpenForm(true);
  };

  // EXPENSES: Cerrar formulario
  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedExpense(null);
  };

  // EXPENSES: Manejar éxito
  const handleSuccess = () => {
    loadExpenses();
    setSnackbar({
      open: true,
      message: selectedExpense ? 'Gasto actualizado exitosamente' : 'Gasto registrado exitosamente',
      severity: 'success',
    });
  };

  // CATEGORIES: Abrir modal de gestión de categorías
  const handleOpenCategoryModal = () => {
    setCategoryModalOpen(true);
  };

  // CATEGORIES: Cerrar modal y recargar categorías
  const handleCloseCategoryModal = () => {
    setCategoryModalOpen(false);
  };

  // CATEGORIES: Actualizar categorías después de cambios
  const handleCategoryUpdate = () => {
    loadCategories();
  };

  // EXPENSES: Eliminar gasto
  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este gasto?')) {
      try {
        await expenseService.delete(id);
        loadExpenses();
        setSnackbar({
          open: true,
          message: 'Gasto eliminado exitosamente',
          severity: 'success',
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.response?.data?.message || 'Error al eliminar gasto',
          severity: 'error',
        });
      }
    }
  };

  // EXPENSES: Ver comprobante
  const handleViewReceipt = (receiptPath) => {
    // Si la ruta ya tiene /uploads/, usarla directamente
    // Si no, construir la ruta completa
    let imageUrl;
    if (receiptPath.startsWith('/uploads/')) {
      imageUrl = `http://localhost:3001${receiptPath}`;
    } else if (receiptPath.startsWith('http')) {
      imageUrl = receiptPath;
    } else {
      // Solo tiene el nombre del archivo, construir ruta completa
      imageUrl = `http://localhost:3001/uploads/expenses/${receiptPath}`;
    }

    setSelectedImage(imageUrl);
    setImageViewerOpen(true);
  };

  // EXPENSES: Cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // EXPENSES: Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // EXPENSES: Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '-';

    // Evitar conversión de zona horaria usando la fecha directamente
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  // EXPENSES: Obtener nombre del mes
  const getMonthName = (month) => {
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return months[month - 1] || '-';
  };

  // EXPENSES: Calcular total
  const calculateTotal = () => {
    return filteredExpenses.reduce((sum, expense) => sum + parseFloat(expense.amount), 0);
  };

  // Obtener datos paginados
  const paginatedExpenses = filteredExpenses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* EXPENSES: Encabezado */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Gastos
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExportToExcel}
            disabled={filteredExpenses.length === 0}
          >
            Exportar a Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Nuevo Gasto
          </Button>
        </Box>
      </Box>

      {/* FILTROS: Panel de filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography variant="h6">
            Filtros
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
            >
              Limpiar
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* Filtro: Categoría */}
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <FormControl sx={{ width: 220 }}>
                <InputLabel>Categoría</InputLabel>
                <Select
                  value={filters.category_id}
                  label="Categoría"
                  onChange={(e) => handleFilterChange('category_id', e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon />}
                onClick={handleOpenCategoryModal}
                sx={{ height: 40 }}
              >
                Gestionar
              </Button>
            </Box>
          </Grid>

          {/* Filtro: Año */}
          <Grid item xs={12} sm={4}>
            <FormControl sx={{ width: 100 }}>
              <InputLabel>Año</InputLabel>
              <Select
                value={filters.year}
                label="Año"
                onChange={(e) => handleFilterChange('year', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: searchYearsBack }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Filtro: Mes */}
          <Grid item xs={12} sm={4}>
            <FormControl sx={{ width: 100 }}>
              <InputLabel>Mes</InputLabel>
              <Select
                value={filters.month}
                label="Mes"
                onChange={(e) => handleFilterChange('month', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                  <MenuItem key={month} value={month}>
                    {getMonthName(month)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Contador de resultados */}
        <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip
            label={`${filteredExpenses.length} ${filteredExpenses.length === 1 ? 'gasto' : 'gastos'}`}
            color="primary"
            size="small"
          />
          <Chip
            label={`Total: ${formatCurrency(calculateTotal())}`}
            color="error"
            size="small"
          />
        </Box>
      </Paper>

      {/* EXPENSES: Mensaje de error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* EXPENSES: Loading */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* EXPENSES: Tabla con ordenamiento */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleRequestSort('id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'category_name'}
                      direction={orderBy === 'category_name' ? order : 'asc'}
                      onClick={() => handleRequestSort('category_name')}
                    >
                      Categoría
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'amount'}
                      direction={orderBy === 'amount' ? order : 'asc'}
                      onClick={() => handleRequestSort('amount')}
                    >
                      Monto
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'expense_date'}
                      direction={orderBy === 'expense_date' ? order : 'asc'}
                      onClick={() => handleRequestSort('expense_date')}
                    >
                      Fecha
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Comprobante</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'created_by_name'}
                      direction={orderBy === 'created_by_name' ? order : 'asc'}
                      onClick={() => handleRequestSort('created_by_name')}
                    >
                      Registrado por
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedExpenses.map((expense) => (
                  <TableRow key={expense.id} hover>
                    <TableCell>{expense.id}</TableCell>
                    <TableCell>
                      <Chip
                        label={expense.category_name}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {expense.description}
                      </Typography>
                      {expense.notes && (
                        <Typography variant="caption" color="text.secondary">
                          {expense.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="error">
                        {formatCurrency(expense.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(expense.expense_date)}</TableCell>
                    <TableCell>
                      {expense.receipt_path ? (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewReceipt(expense.receipt_path)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Sin comprobante
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {expense.created_by_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(expense)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(expense.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

                {/* FILA DE TOTAL */}
                {filteredExpenses.length > 0 && (
                  <TableRow sx={{ bgcolor: 'grey.100', '& td': { fontWeight: 'bold' } }}>
                    <TableCell colSpan={3} align="right">
                      TOTAL ({filteredExpenses.length} {filteredExpenses.length === 1 ? 'gasto' : 'gastos'}):
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold" color="error.main" variant="h6">
                        {formatCurrency(calculateTotal())}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={4}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* PAGINACIÓN */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={filteredExpenses.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </TableContainer>

          {/* EXPENSES: Mensaje si no hay gastos */}
          {filteredExpenses.length === 0 && !loading && (
            <Paper sx={{ p: 3, mt: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {filters.category_id || filters.year || filters.month
                  ? 'No se encontraron gastos con los filtros aplicados'
                  : 'No hay gastos registrados'}
              </Typography>
            </Paper>
          )}
        </>
      )}

      {/* EXPENSES: Formulario */}
      <ExpenseForm
        open={openForm}
        onClose={handleCloseForm}
        expense={selectedExpense}
        onSuccess={handleSuccess}
      />

      {/* EXPENSES: Visor de imágenes */}
      <ImageViewer
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        imageUrl={selectedImage}
      />

      {/* CATEGORIES: Modal de gestión de categorías */}
      <CategoryModal
        open={categoryModalOpen}
        onClose={handleCloseCategoryModal}
        onUpdate={handleCategoryUpdate}
      />

      {/* EXPENSES: Notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ExpenseList;
